import React from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Briefcase } from 'lucide-react';

const SkillsExperience = ({ data }) => {
  return (
    <section id="skills-experience" className="py-24 bg-[#0a0a0a] border-t border-[#2a2a2a]">
      <div className="max-w-6xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Skills & Experience</h2>
          <p className="text-[#a3a3a3] text-lg">Explore my technical expertise and professional journey</p>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="experience" className="w-full">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-12 bg-[#161616] border border-[#2a2a2a] p-1">
            <TabsTrigger 
              value="experience"
              className="data-[state=active]:bg-white data-[state=active]:text-black text-[#a3a3a3] transition-all duration-300"
            >
              Work Experience
            </TabsTrigger>
            <TabsTrigger 
              value="skills"
              className="data-[state=active]:bg-white data-[state=active]:text-black text-[#a3a3a3] transition-all duration-300"
            >
              Technical Skills
            </TabsTrigger>
          </TabsList>

          {/* Work Experience Tab */}
          <TabsContent value="experience" className="mt-8">
            <div className="relative">
              {/* Vertical Line */}
              <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 h-full w-px bg-[#2a2a2a]"></div>

              <div className="space-y-12">
                {data.workExperience.map((job, index) => (
                  <div key={job.id} className={`relative flex items-center ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    {/* Timeline Dot */}
                    <div className="hidden md:flex absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-white rounded-full border-4 border-[#0a0a0a] z-10"></div>

                    {/* Content Card */}
                    <div className={`w-full md:w-[calc(50%-2rem)] ${index % 2 === 0 ? 'md:pr-12' : 'md:pl-12'}`}>
                      <Card className="bg-[#161616] border-[#2a2a2a] p-8 hover:border-white transition-all duration-300">
                        <div className="flex items-start gap-4 mb-4">
                          <div className="bg-white text-black p-3 rounded-lg">
                            <Briefcase className="h-6 w-6" />
                          </div>
                          <div className="flex-1">
                            <h3 className="text-xl font-bold text-white mb-1">{job.title}</h3>
                            <p className="text-[#a3a3a3] text-lg mb-2">{job.company}</p>
                            <p className="text-sm text-[#666666]">{job.period}</p>
                          </div>
                        </div>
                        <ul className="space-y-2 mt-6">
                          {job.responsibilities.slice(0, 4).map((resp, idx) => (
                            <li key={idx} className="text-[#a3a3a3] text-sm leading-relaxed flex items-start">
                              <span className="text-white mr-2 mt-1">•</span>
                              <span>{resp}</span>
                            </li>
                          ))}
                        </ul>
                      </Card>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* Technical Skills Tab */}
          <TabsContent value="skills" className="mt-8">
            <div className="grid md:grid-cols-2 gap-8">
              {Object.entries(data.skills).map(([category, skillsList]) => (
                <Card key={category} className="bg-[#161616] border-[#2a2a2a] p-8 hover:border-white transition-all duration-300">
                  <h3 className="text-xl font-bold text-white mb-6">{category}</h3>
                  <div className="flex flex-wrap gap-2">
                    {skillsList.map((skill, index) => (
                      <Badge 
                        key={index}
                        variant="outline"
                        className="bg-[#0a0a0a] border-[#2a2a2a] text-[#a3a3a3] hover:bg-white hover:text-black hover:border-white transition-all duration-300 px-3 py-1.5 text-sm font-mono cursor-default"
                      >
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

export default SkillsExperience;
